"use client";

import { useForm, FormProvider } from "react-hook-form";
import type { Resolver } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { type infer as ZodInfer } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { schema } from "./schema/schema";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { ExpensesService } from "@/services/ExpenseService";
import { DatePickerComponent } from "@/components/DatePickerComponent";
import { Trash2, Receipt } from "lucide-react";
import CurrencyInput from "@/components/CurrencyInput";
import {
  type ExpensesInput,
  ExpensesPaymentDetailsMethod,
} from "@/interfaces/expenses";
import CustomCombobox from "@/components/CustomCombobox";
import MultiTagSelector from "@/components/MultiTagSelector";
import { Separator } from "@/components/ui/separator";
import { orderPaymentDetailsMethodTranslations } from "@/interfaces/payments";
import { localDate } from "@/util/dates";
// Substituir formatações monetárias por numberToMoney
import { numberToMoney } from "@/util/moneyConversion";

type ExpenseFormValues = ZodInfer<typeof schema>;

export default function ExpenseForm() {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const service = new ExpensesService();

  const form = useForm<ExpenseFormValues, undefined, ExpenseFormValues>({
    resolver: zodResolver(schema) as Resolver<ExpenseFormValues, undefined, ExpenseFormValues>,
    defaultValues: {
      name: "",
      expenseDate: localDate(),
    },
  });

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Já estamos com isLoading = true desde o início
        setIsEditing(!!id);

        // Se estiver editando, buscar os dados da despesa
        if (id) {
          const fetchedExpense = await service.findById(id);

          // Preenche os valores do formulário com os dados buscados
          form.reset({
            ...fetchedExpense,
            tags: fetchedExpense.tags as string[],
            expenseDate: localDate(fetchedExpense.expenseDate),
            paymentDetails: fetchedExpense.paymentDetails?.map((payment) => ({
              ...payment,
              paymentDate: localDate(payment.paymentDate),
            })),
          });
        }
      } catch (error) {
        console.error("Erro ao carregar dados:", error);
      } finally {
        // Só definir isLoading como false depois de carregar todos os dados
        setIsLoading(false);
      }
    };

    fetchInitialData();

    // Cleanup function para quando o componente for desmontado
    return () => {
      // Resetar o estado de loading para true para a próxima vez que o componente for montado
      setIsLoading(true);
    };
  }, [id, form]);

  // Função de submit do formulário
  async function onSubmit(values: ExpenseFormValues) {
    try {
      setIsLoading(true);
      const input: ExpensesInput = {
        ...values,
        paymentDetails: values.paymentDetails?.map((paymentDetail) => ({
          ...paymentDetail,
          paymentDate: localDate(values.expenseDate),
        })),
      };
      if (isEditing && id) {
        await service.update(id, input);
      } else {
        await service.create(input);
      }
      navigate("/despesas");
    } catch (error) {
      console.error("Erro ao salvar despesa:", error);
    } finally {
      setIsLoading(false);
    }
  }

  const watchPaymentDetails = form.watch("paymentDetails") || [];

  const addPaymentRow = () => {
    form.setValue("paymentDetails", [
      ...watchPaymentDetails,
      {
        method: ExpensesPaymentDetailsMethod.CASH,
        paymentDate: localDate(),
        amount: 0,
      },
    ]);
  };

  const removePaymentRow = (index: number) => {
    const updatedPayments = watchPaymentDetails.filter((_, i) => i !== index);
    form.setValue("paymentDetails", updatedPayments);
  };

  // Substituir no cálculo do total pago
  const calculateTotalPaid = () => {
    return numberToMoney(
      Number(
        watchPaymentDetails.reduce<number>(
          (sum, payment) => sum + (payment.amount || 0),
          0
        )
      )
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <FormProvider {...form}>
      <div className="p-4 sm:p-6 max-w-2xl mx-auto">
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Card className="overflow-hidden border-none shadow-md">
            <CardHeader className="bg-gradient-to-r from-[#EE7B11] to-[#f59642] text-white">
              <div className="flex items-center gap-3">
                <Receipt className="h-6 w-6" />
                <CardTitle className="text-xl">
                  {isEditing ? "Editar Despesa" : "Nova Despesa"}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome da despesa</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Nome da despesa"
                        type="text"
                        {...field}
                        className="border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11]"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="expenseDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Data da despesa</FormLabel>
                    <FormControl>
                      <DatePickerComponent
                        {...field}
                        id="expenseDate"
                        selected={field.value}
                        onSelect={(date) => field.onChange(date)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="tags"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tags</FormLabel>
                    <FormControl>
                      <MultiTagSelector
                        value={field.value || []}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Separator className="my-4" />

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Pagamentos</h3>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addPaymentRow}
                    className="border-[#EE7B11] text-[#EE7B11] hover:bg-[#EE7B11]/10"
                  >
                    Adicionar Pagamento
                  </Button>
                </div>

                {watchPaymentDetails.length === 0 && (
                  <div className="text-center py-8 border rounded-lg bg-gray-50">
                    <p className="text-gray-500">Nenhum pagamento cadastrado</p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addPaymentRow}
                      className="mt-2 border-[#EE7B11] text-[#EE7B11] hover:bg-[#EE7B11]/10"
                    >
                      Adicionar Pagamento
                    </Button>
                  </div>
                )}

                {watchPaymentDetails.map((_, index) => (
                  <div
                    key={index}
                    className="space-y-2 border p-4 rounded-lg bg-gray-50"
                  >
                    <div className="flex justify-between items-center">
                      <h4 className="text-md font-medium">{`Pagamento ${
                        index + 1
                      }`}</h4>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => removePaymentRow(index)}
                        className="h-8 w-8 border-red-500 text-red-500 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name={`paymentDetails.${index}.method`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Método de Pagamento</FormLabel>
                            <FormControl>
                              <CustomCombobox
                                {...field}
                                key={`paymentDetails.${index}.method`}
                                options={Object.entries(
                                  orderPaymentDetailsMethodTranslations
                                ).map(([value, label]) => ({
                                  value,
                                  label,
                                }))}
                                className="border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11]"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name={`paymentDetails.${index}.amount`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Valor</FormLabel>
                            <FormControl>
                              <CurrencyInput
                                {...field}
                                className="border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11]"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                ))}

                {watchPaymentDetails.length > 0 && (
                  <div className="p-4 bg-gray-50 rounded-lg mt-4">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Total Pago:</span>
                      {/* Substituir na exibição do total */}
                      <span className="font-bold text-[#EE7B11] text-lg">
                        {calculateTotalPaid()}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
            <CardFooter className="border-t p-4 sm:p-6 flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/despesas")}
                className="border-gray-200 text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="bg-[#EE7B11] hover:bg-[#d06a0a] text-white"
              >
                {isEditing ? "Atualizar" : "Salvar"}
              </Button>
            </CardFooter>
          </Card>
        </form>
      </div>
    </FormProvider>
  );
}









